import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

const Section = ({ id, title, children }) => (
  <section id={id} className="space-y-4">
    <h2 className="text-xl font-semibold text-foreground border-b border-border pb-3">{title}</h2>
    <div className="space-y-3 text-muted-foreground leading-relaxed text-sm">{children}</div>
  </section>
);

const Terms = () => {
  const lastUpdated = 'June 2026';

  return (
    <div className="min-h-screen bg-white py-20 px-6 lg:px-20">
      <SEO
        title="Terms of Service — FashionSpyder"
        description="FashionSpyder's terms of service. Review our terms for using the fashion HR intelligence platform, subscription plans, and candidate data policies."
        path="/terms"
      />
      <div className="max-w-3xl mx-auto">

        <div className="mb-14">
          <span className="text-xs uppercase tracking-widest text-muted-foreground" style={{ fontFamily: MONO }}>
            Legal · Terms of Service
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold mt-4 mb-3 tracking-tight" style={{ fontFamily: SERIF }}>
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground" style={{ fontFamily: MONO }}>
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="space-y-12">

          <Section title="1. Agreement to Terms">
            <p>
              By accessing or using FashionSpyder ("Platform", "we", "our"), you confirm that you have read,
              understood, and agree to be bound by these Terms of Service ("Terms") and our{' '}
              <Link to="/privacy" className="text-foreground underline underline-offset-2">Privacy Policy</Link>.
              If you do not agree, you must not access or use the Platform.
            </p>
            <p>
              FashionSpyder is operated under the <strong className="text-foreground">Flence ecosystem</strong>,
              a product developed and owned by <strong className="text-foreground">Influentive Ltd, Malta</strong>.
              These Terms constitute a legally binding agreement between you
              (the subscribing organisation or individual) and Influentive Ltd / FashionSpyder.
            </p>
          </Section>

          <Section title="2. Eligibility & Account Registration">
            <p>
              FashionSpyder is a <strong className="text-foreground">B2B platform</strong> available exclusively
              to HR professionals, talent acquisition managers, and hiring teams at fashion brands, agencies,
              and related businesses. Consumer use is not permitted.
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>You must register using a verifiable <strong className="text-foreground">company email address</strong>. Free email providers (Gmail, Yahoo, Hotmail, etc.) are not accepted.</li>
              <li>You must be at least 18 years old and legally authorised to enter into contracts on behalf of your organisation.</li>
              <li>You are responsible for maintaining the confidentiality of your credentials and for all activity under your account.</li>
              <li>You must notify us immediately of any unauthorised access at <a href="mailto:security@fashionspyder.com" className="text-foreground underline underline-offset-2">security@fashionspyder.com</a>.</li>
            </ul>
            <p>
              We reserve the right to refuse access to any person or organisation at our discretion, including
              during the pre-launch phase where access is managed by invitation.
            </p>
          </Section>

          <Section title="3. Subscription Plans & Billing">
            <p>
              FashionSpyder offers the following subscription tiers:
            </p>
            <div className="space-y-3">
              <div className="border border-border p-4">
                <p className="font-semibold text-foreground mb-1">RECON</p>
                <p>Full gallery access, basic filters, Kanban pipeline, HR dashboard. €149/month or €1,252/year.</p>
              </div>
              <div className="border border-border p-4">
                <p className="font-semibold text-foreground mb-1">INTELLIGENCE</p>
                <p>All RECON features plus AI Matchmaker, AI Talent Brief, AI DNA panel, advanced filters, Compare & Decide. €349/month or €2,930/year.</p>
              </div>
              <div className="border border-border p-4">
                <p className="font-semibold text-foreground mb-1">White Glove (Dedicated Hiring)</p>
                <p>A bespoke, managed search service. Pricing and terms are disclosed in a separate Service Agreement, provided only to accepted clients after review. See Section 9.</p>
              </div>
            </div>
            <ul className="list-disc pl-4 space-y-2 mt-2">
              <li>Subscription fees are billed in advance on a monthly or annual basis via Stripe.</li>
              <li>All fees are stated in EUR and exclude applicable VAT unless otherwise specified.</li>
              <li>Fees are non-refundable except as required by EU consumer law or as expressly stated herein.</li>
              <li>We reserve the right to adjust pricing with 30 days' written notice. Price changes will not affect the current billing period.</li>
            </ul>
          </Section>

          <Section title="4. Cancellation & Refund Policy">
            <p>
              You may cancel your subscription at any time from your Account dashboard. Upon cancellation:
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>Your access to the Platform continues until the end of the current paid billing period.</li>
              <li>No further charges will be made after cancellation.</li>
              <li>We do not provide prorated refunds for unused days within a billing period.</li>
              <li>Annual subscriptions cancelled within 14 days of initial purchase may be eligible for a refund under EU consumer cooling-off rights — contact <a href="mailto:billing@fashionspyder.com" className="text-foreground underline underline-offset-2">billing@fashionspyder.com</a>.</li>
            </ul>
          </Section>

          <Section title="5. Permitted & Prohibited Use">
            <p>
              The Platform is licensed to you on a non-exclusive, non-transferable basis for lawful internal
              hiring purposes only. You agree <strong className="text-foreground">not to</strong>:
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>Resell, sublicense, or transfer access to any third party.</li>
              <li>Scrape, harvest, or systematically extract candidate data in bulk.</li>
              <li>Use candidate data for any purpose other than evaluating professionals for legitimate employment opportunities.</li>
              <li>Contact candidates outside of FashionSpyder's built-in messaging system or the Flence platform.</li>
              <li>Circumvent or attempt to bypass authentication, security measures, or subscription gates.</li>
              <li>Use the Platform in any way that violates GDPR, employment law, anti-discrimination law, or any applicable regulation.</li>
              <li>Upload, transmit, or share content that is unlawful, discriminatory, offensive, or infringes third-party intellectual property rights.</li>
            </ul>
          </Section>

          <Section title="6. Candidate Data & GDPR Compliance">
            <p>
              All candidate profiles on FashionSpyder originate from the Flence platform, where professionals
              have consented to their data being shared with verified HR partners.
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>You act as a <strong className="text-foreground">data controller</strong> in respect of any candidate data you export, store, or process outside the Platform.</li>
              <li>You agree to handle candidate data in accordance with the GDPR (Regulation EU 2016/679) and applicable data protection law.</li>
              <li>You must not use candidate data for profiling, automated decision-making, or any purpose not directly related to evaluating them for the specific role for which they were identified.</li>
              <li>Candidate contact details are not exposed on the Platform. You must use FashionSpyder's internal messaging or the Flence platform to initiate contact.</li>
              <li>A Data Processing Agreement (DPA) is available on request at <a href="mailto:privacy@fashionspyder.com" className="text-foreground underline underline-offset-2">privacy@fashionspyder.com</a>.</li>
            </ul>
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              All content, software, design, trademarks, and AI methodology on FashionSpyder are the exclusive
              property of Flence / FashionSpyder or its licensors. Nothing in these Terms grants you any
              ownership rights. You may not copy, reproduce, modify, distribute, or create derivative works
              based on any Platform content without express written permission.
            </p>
            <p>
              Candidate profiles displayed on the Platform remain the intellectual property of the respective
              professionals. FashionSpyder is licensed to display this content under its agreement with Flence.
            </p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law:
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>FashionSpyder provides the Platform "as is" and makes no warranties as to the accuracy, completeness, or suitability of candidate profiles for any specific role.</li>
              <li>FashionSpyder is not liable for any hiring decisions made on the basis of Platform data.</li>
              <li>FashionSpyder's total aggregate liability to you shall not exceed the fees paid by you in the 3 months preceding the relevant claim.</li>
              <li>FashionSpyder shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</li>
            </ul>
            <p>
              Nothing in these Terms limits liability for death, personal injury caused by negligence, or
              fraudulent misrepresentation. EU consumer rights are not affected.
            </p>
          </Section>

          <Section title="9. AI-Assisted Evaluations, StyleDNA & Human Oversight">
            <p>
              FashionSpyder's scoring, ranking, matching, and analytical features — including the
              <strong className="text-foreground"> StyleDNA</strong>, Career Visibility Engine / Visibility Index,
              AI Matchmaker, AI Talent Brief, AI Analyst ("Ask the Analyst"), Role-Fit scores, and any
              other automated insight or query response (collectively, the "AI Features") — are produced
              by FashionSpyder's <strong className="text-foreground">proprietary algorithms in combination with
              third-party artificial-intelligence and large-language-model technologies</strong>.
            </p>
            <ul className="list-disc pl-4 space-y-2">
              <li>
                The AI Features are provided <strong className="text-foreground">for informational and
                decision-support purposes only</strong>. They are designed to assist and accelerate the work
                of your hiring team — not to make hiring decisions for you.
              </li>
              <li>
                Artificial intelligence and automated scoring are inherently probabilistic and
                <strong className="text-foreground"> can produce incomplete, inaccurate, or misleading
                results</strong> ("hallucinations", mis-classifications, or errors). Outputs may not reflect a
                candidate's true skills, availability, or suitability for a given role.
              </li>
              <li>
                You agree that all AI Feature outputs are <strong className="text-foreground">subject to human
                review and verification by qualified members of your team</strong> before any decision is taken.
                The AI Features must <strong className="text-foreground">not</strong> be used as the sole or
                automated basis for hiring, rejection, or any decision producing legal or similarly
                significant effects on an individual (Article 22 GDPR).
              </li>
              <li>
                You remain solely responsible for ensuring that your use of the AI Features complies with all
                applicable employment, equal-opportunity, anti-discrimination, and data-protection laws.
              </li>
              <li>
                FashionSpyder makes <strong className="text-foreground">no warranty</strong> as to the accuracy,
                completeness, reliability, or fitness for a particular purpose of any AI Feature output, and,
                to the maximum extent permitted by law, accepts no liability for decisions made in reliance on
                it. This clause supplements Section 8 (Limitation of Liability).
              </li>
            </ul>
            <p className="text-sm text-muted-foreground italic">
              In short: our StyleDNA and AI insights are built on our own proprietary methodology to make your
              team faster and better informed — but AI can make mistakes, and these tools are meant to support,
              never replace, human judgement and supervision.
            </p>
          </Section>

          <Section title="10. White Glove Dedicated Hiring Service">
            <p>
              The White Glove service is governed by a separate Service Agreement issued to each accepted
              client. Key terms include a monthly retainer, a refundable placement deposit, and a
              success-based placement fee (percentage of the hired candidate's first-year gross annual salary).
              Full terms, including the non-circumvention clause, placement guarantee, and governing law
              provisions, are disclosed in the individual Service Agreement. These Terms take precedence
              over the Service Agreement only where they are not expressly varied by it.
            </p>
          </Section>

          <Section title="11. Analytics & Marketing Technologies">
            <p>
              FashionSpyder uses third-party analytics and marketing tools including Google Analytics 4,
              Meta (Facebook) Pixel, and Pinterest Tag. These technologies are activated only with your
              consent as obtained via our cookie banner. See our{' '}
              <Link to="/cookies" className="text-foreground underline underline-offset-2">Cookie Policy</Link>{' '}
              for full details of each tool, the data collected, and how to opt out.
            </p>
          </Section>

          <Section title="12. Modifications to the Service & Terms">
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Platform at any time.
              We will provide reasonable notice of material changes. Continued use of the Platform after
              changes take effect constitutes acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="13. Governing Law & Jurisdiction">
            <p>
              These Terms are governed by the laws of <strong className="text-foreground">Italy</strong> and,
              where applicable, the laws of the European Union. Any dispute arising from or in connection with
              these Terms shall be subject to the exclusive jurisdiction of the courts of{' '}
              <strong className="text-foreground">Milan, Italy</strong>, unless mandatory EU consumer law
              provides otherwise.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>
              For legal and contractual matters:{' '}
              <a href="mailto:legal@fashionspyder.com" className="text-foreground underline underline-offset-2">
                legal@fashionspyder.com
              </a>
            </p>
            <p>
              For billing and subscription matters:{' '}
              <a href="mailto:billing@fashionspyder.com" className="text-foreground underline underline-offset-2">
                billing@fashionspyder.com
              </a>
            </p>
            <p>
              For data protection matters:{' '}
              <a href="mailto:privacy@fashionspyder.com" className="text-foreground underline underline-offset-2">
                privacy@fashionspyder.com
              </a>
            </p>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default Terms;
